/**
 * Environment Validation Module
 *
 * PURPOSE: Prevent accidental database connection to wrong environment
 *
 * CRITICAL RULES:
 * 1. NEVER switch connection URL based on hostname
 * 2. ALWAYS validate project-ref matches expected value
 * 3. THROW error on mismatch (except local environment)
 * 4. Log all validation results for debugging
 */

export type Environment = 'local' | 'staging' | 'preview' | 'production';

export interface EnvironmentInfo {
  environment: Environment;
  hostname: string;
  projectRef: string | null;
  expectedRef: string | null;
  isValid: boolean;
  error?: string;
}

/**
 * Detect current environment based on hostname
 * Priority: staging > production > preview > local
 */
export function detectEnvironment(hostname: string): Environment {
  // Check for staging first (highest priority)
  if (hostname.includes('stg-') || hostname.includes('staging')) {
    return 'staging';
  }

  // Check for production domain
  if (
    (hostname.includes('exworks.io') && !hostname.includes('stg-')) ||
    hostname === 'telemetry-manual.vercel.app'
  ) {
    return 'production';
  }

  // Check for Vercel preview deployments
  if (hostname.includes('vercel.app')) {
    return 'preview';
  }

  // Default to local
  return 'local';
}

/**
 * Extract project reference from Supabase URL
 * Example: https://adbpzrifvgkkwkuyhiav.supabase.co → adbpzrifvgkkwkuyhiav
 */
export function extractProjectRef(supabaseUrl: string): string | null {
  try {
    const match = supabaseUrl.match(/https:\/\/([a-z0-9]+)\.supabase\.co/);
    return match ? match[1] : null;
  } catch (error) {
    console.error('Failed to extract project ref:', error);
    return null;
  }
}

/**
 * Get expected project reference for current environment
 */
export function getExpectedProjectRef(environment: Environment): string | null {
  const refs = {
    local: null, // No validation for local development
    staging: import.meta.env.VITE_EXPECTED_STAGING_PROJECT_REF || null,
    preview: import.meta.env.VITE_EXPECTED_PREVIEW_PROJECT_REF || null,
    production: import.meta.env.VITE_EXPECTED_PROD_PROJECT_REF || null,
  };

  return refs[environment];
}

/**
 * Validate environment configuration
 * Returns validation result with detailed information
 */
export function validateEnvironment(): EnvironmentInfo {
  const hostname = window.location.hostname;
  const environment = detectEnvironment(hostname);
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || '';
  const projectRef = extractProjectRef(supabaseUrl);
  const expectedRef = getExpectedProjectRef(environment);

  // Local environment: skip validation
  if (environment === 'local') {
    return {
      environment,
      hostname,
      projectRef,
      expectedRef,
      isValid: true,
    };
  }

  // Non-local environment: strict validation required
  let isValid = true;
  let error: string | undefined;

  // Check if project ref was extracted
  if (!projectRef) {
    isValid = false;
    error = 'Failed to extract project reference from SUPABASE_URL';
  }

  // Check if expected ref is configured
  else if (!expectedRef) {
    isValid = false;
    error = `Missing VITE_EXPECTED_${environment.toUpperCase()}_PROJECT_REF environment variable`;
  }

  // Check if refs match
  else if (projectRef !== expectedRef) {
    isValid = false;
    error = `Project reference mismatch: expected '${expectedRef}' but got '${projectRef}'`;
  }

  return {
    environment,
    hostname,
    projectRef,
    expectedRef,
    isValid,
    error,
  };
}

/**
 * Log environment information to console
 * Only logs if VITE_ENABLE_CONSOLE_LOG is true
 */
export function logEnvironmentInfo(info: EnvironmentInfo): void {
  const enableLog = import.meta.env.VITE_ENABLE_CONSOLE_LOG === 'true';

  if (!enableLog) {
    return;
  }

  console.group('🔍 Environment Validation');
  console.log('Environment:', info.environment);
  console.log('Hostname:', info.hostname);
  console.log('Project Ref:', info.projectRef);
  console.log('Expected Ref:', info.expectedRef);
  console.log('Valid:', info.isValid ? '✅' : '❌');

  if (info.error) {
    console.error('Error:', info.error);
  }

  console.groupEnd();
}

/**
 * Validate and throw error if validation fails
 * This function should be called during app initialization
 */
export function assertValidEnvironment(): EnvironmentInfo {
  const info = validateEnvironment();

  // Always log validation result
  logEnvironmentInfo(info);

  // Throw error if validation fails (except local)
  if (!info.isValid && info.environment !== 'local') {
    throw new Error(
      `Environment validation failed: ${info.error}\n\n` +
      `Environment: ${info.environment}\n` +
      `Hostname: ${info.hostname}\n` +
      `Project Ref: ${info.projectRef}\n` +
      `Expected Ref: ${info.expectedRef}\n\n` +
      `This error prevents accidental database connection to wrong environment.\n` +
      `Please check Vercel environment variables configuration.`
    );
  }

  return info;
}
